import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { inputCls, btnPrimary, cardCls } from "./styles";
import { ImageField } from "./ImageField";

const Field = ({ label, value, onChange, testid, textarea, dir }) => (
  <div>
    <label className="text-xs text-zinc-500 block mb-1.5">{label}</label>
    {textarea ? (
      <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={3} className={inputCls} />
    ) : (
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} dir={dir} className={dir === "ltr" ? `${inputCls} text-left` : inputCls} />
    )}
  </div>
);

const ListField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-xs text-zinc-500 block mb-1.5">{label} — كل عنصر في سطر</label>
    <textarea
      data-testid={testid}
      value={(value || []).join("\n")}
      onChange={(e) => onChange(e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))}
      rows={5}
      className={inputCls}
    />
  </div>
);

const Section = ({ title, children }) => (
  <div className={cardCls}>
    <h3 className="font-display font-bold text-white mb-4">{title}</h3>
    <div className="grid sm:grid-cols-2 gap-4">{children}</div>
  </div>
);

export const SettingsTab = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [pwSaving, setPwSaving] = useState(false);

  const changePw = async () => {
    setPwSaving(true);
    try {
      await api.post("/admin/change-password", pw);
      toast.success("تم تغيير كلمة المرور بنجاح — استخدم الجديدة في الدخول القادم");
      setPw({ current_password: "", new_password: "" });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setPwSaving(false);
    }
  };

  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!form) return <div className="text-zinc-500 text-sm py-10 text-center">جارٍ التحميل...</div>;

  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("تم حفظ الإعدادات — التغييرات ظاهرة في الموقع فورًا");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="settings-tab">
      <Section title="معلومات التواصل">
        <Field label="رقم الاتصال (يظهر في كل الموقع)" value={form.phone_display} onChange={set("phone_display")} testid="settings-phone" dir="ltr" />
        <Field label="رقم الواتساب بالصيغة الدولية" value={form.whatsapp} onChange={set("whatsapp")} testid="settings-whatsapp" dir="ltr" />
        <Field label="ساعات العمل" value={form.hours} onChange={set("hours")} testid="settings-hours" />
        <Field label="نطاق الخدمة" value={form.location_text} onChange={set("location_text")} testid="settings-location" />
      </Section>

      <Section title="الواجهة الرئيسية (الهيرو)">
        <Field label="الشارة العلوية" value={form.hero_badge} onChange={set("hero_badge")} testid="settings-hero-badge" />
        <Field label="السطر الأول" value={form.hero_line1} onChange={set("hero_line1")} testid="settings-hero-line1" />
        <Field label="السطر الثاني (باللون الذهبي)" value={form.hero_line2} onChange={set("hero_line2")} testid="settings-hero-line2" />
        <Field label="السطر الثالث" value={form.hero_line3} onChange={set("hero_line3")} testid="settings-hero-line3" />
        <div className="sm:col-span-2">
          <Field label="الوصف" value={form.hero_desc} onChange={set("hero_desc")} testid="settings-hero-desc" textarea />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs text-zinc-500 block mb-1.5">صورة الهيرو</label>
          <ImageField value={form.hero_image} onChange={set("hero_image")} testid="settings-hero-image" />
        </div>
      </Section>

      <Section title="قسم الطوارئ">
        <Field label="العنوان" value={form.emergency_title} onChange={set("emergency_title")} testid="settings-emergency-title" />
        <Field label="النص الفرعي" value={form.emergency_sub} onChange={set("emergency_sub")} testid="settings-emergency-sub" />
      </Section>

      <Section title="القوائم المتحركة وخيارات المعالج">
        <ListField label="شريط الأخبار المتحرك" value={form.marquee_items} onChange={set("marquee_items")} testid="settings-marquee" />
        <ListField label="أنواع السيارات في المعالج" value={form.car_brands} onChange={set("car_brands")} testid="settings-brands" />
        <ListField label="مقاسات الإطارات في المعالج" value={form.tire_sizes} onChange={set("tire_sizes")} testid="settings-sizes" />
      </Section>

      <Section title="تغيير كلمة المرور">
        <div>
          <label className="text-xs text-zinc-500 block mb-1.5">كلمة المرور الحالية</label>
          <input data-testid="pw-current-input" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className="text-xs text-zinc-500 block mb-1.5">كلمة المرور الجديدة (٨ أحرف فأكثر)</label>
          <input data-testid="pw-new-input" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <button
            data-testid="pw-change-btn"
            onClick={changePw}
            disabled={pwSaving || !pw.current_password || pw.new_password.length < 8}
            className={btnPrimary}
          >
            {pwSaving ? "جارٍ التغيير..." : "تغيير كلمة المرور"}
          </button>
        </div>
      </Section>

      <button data-testid="settings-save-btn" onClick={save} disabled={saving} className={`${btnPrimary} flex items-center gap-2`}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        {saving ? "جارٍ الحفظ..." : "حفظ الإعدادات"}
      </button>
    </div>
  );
};
